import React, { useRef, useState } from 'react'
import useFocusWhenOpen from 'hooks/useFocusWhenOpen'

export interface InlineRename {
  /** The id of what's being renamed, or null when no rename is open. */
  renamingId: string | null
  /** Open the rename input for `id`, pre-filled with its current `name`. */
  start: (id: string, name: string) => void
  /** Spread onto the rename input. */
  inputProps: {
    ref: React.RefObject<HTMLInputElement | null>
    value: string
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onBlur: () => void
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  }
}

/**
 * Double-click inline rename, shared by the toolbar's view tabs and the table page's name.
 * The input opens focused with its text selected; Enter or clicking away commits, Escape
 * discards. An empty or unchanged name is discarded rather than saved, and the input closes
 * as soon as it commits — `onCommit` saves the trimmed name and reports any failure itself.
 */
export const useInlineRename = (onCommit: (id: string, name: string) => void): InlineRename => {
  const [renaming, setRenaming] = useState<{ id: string; original: string } | null>(null)
  const [draft, setDraft] = useState<string>('')
  // Escape unmounts the input, which also fires onBlur; flag the cancel so the blur
  // handler doesn't then commit the discarded draft.
  const cancelling = useRef<boolean>(false)
  const renamingId = renaming?.id ?? null
  const inputRef = useFocusWhenOpen(renamingId !== null)

  const start = (id: string, name: string): void => {
    // prevent swallowing the next genuine commit
    cancelling.current = false
    setDraft(name)
    setRenaming({ id, original: name })
  }

  const commit = (): void => {
    if (cancelling.current) {
      cancelling.current = false
      setRenaming(null)
      return
    }
    setRenaming(null)
    if (renaming === null) return
    const trimmed = draft.trim()
    // Empty or unchanged names are discarded — the original name stays in place.
    if (trimmed === '' || trimmed === renaming.original) return
    onCommit(renaming.id, trimmed)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Commit via the blur handler so there's a single commit path.
      inputRef.current?.blur()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelling.current = true
      setRenaming(null)
    }
  }

  return {
    renamingId,
    start,
    inputProps: {
      ref: inputRef,
      value: draft,
      onChange: (event) => setDraft(event.target.value),
      onBlur: commit,
      onKeyDown
    }
  }
}
